import { ref, type App } from 'vue';
import { useNotification } from '@asabdev/asab-ui';

// Global error state
export const globalError = ref<Error | null>(null);
export const isErrorBoundaryActive = ref(false);

/**
 * Global error handler for uncaught errors
 */
export function setupGlobalErrorHandler(app: App) {
  // Vue error handler
  app.config.errorHandler = (err: unknown, instance, info) => {
    const error = err instanceof Error ? err : new Error(String(err));
    console.error('Vue Error:', error);
    console.error('Component Instance:', instance);
    console.error('Error Info:', info);

    handleGlobalError(error, 'Vue Component Error');
  };

  // Global unhandled promise rejection handler
  window.addEventListener('unhandledrejection', event => {
    console.error('Unhandled Promise Rejection:', event.reason);

    // Try to extract meaningful error information
    let error: Error;
    if (event.reason instanceof Error) {
      error = event.reason;
    } else {
      error = new Error(String(event.reason));
    }

    handleGlobalError(error, 'Unhandled Promise Rejection');

    // Prevent the error from being logged to console again
    event.preventDefault();
  });

  // Global JavaScript error handler
  window.addEventListener('error', event => {
    console.error('Global JavaScript Error:', event.error);

    const error = event.error || new Error(event.message);
    handleGlobalError(error, 'JavaScript Error');
  });
}

/**
 * Handle global errors with user-friendly notifications
 */
function handleGlobalError(error: Error, context: string) {
  const { showError } = useNotification();

  // Set global error state
  globalError.value = error;
  isErrorBoundaryActive.value = true;

  // Determine appropriate user message based on error type
  let userMessage = 'An unexpected error occurred. Please try again.';
  let title = 'Application Error';

  if (error.name === 'ChunkLoadError' || error.message.includes('Loading chunk')) {
    userMessage = 'Failed to load application resources. Please refresh the page.';
    title = 'Loading Error';
  } else if (error.name === 'NetworkError' || error.message.includes('network')) {
    userMessage = 'Network error occurred. Please check your connection and try again.';
    title = 'Network Error';
  } else if (error.name === 'TypeError' && error.message.includes('Cannot read properties')) {
    userMessage = 'An unexpected error occurred. Please refresh the page.';
    title = 'Application Error';
  }

  // Show error notification
  showError(userMessage, title);

  // Log detailed error information for debugging
  logErrorDetails(error, context);

  // Clear error state after a delay
  setTimeout(() => {
    clearGlobalError();
  }, 5000);
}

/**
 * Clear global error state
 */
export function clearGlobalError() {
  globalError.value = null;
  isErrorBoundaryActive.value = false;
}

/**
 * Log detailed error information for debugging and monitoring
 */
function logErrorDetails(error: Error, context: string) {
  const errorDetails = {
    name: error.name,
    message: error.message,
    stack: error.stack,
    context,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    userId: getUserId(), // Helper to get current user ID if available
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.group('🔴 Global Error Details');
    console.error('Error Details:', errorDetails);
    console.error('Original Error:', error);
    console.groupEnd();
  }

  // In production, send to error tracking service
  // Example: Sentry, LogRocket, Bugsnag, etc.
  if (process.env.NODE_ENV === 'production') {
    // sendToErrorTracking(errorDetails);
  }
}

/**
 * Helper to get current user ID for error tracking
 */
function getUserId(): string | null {
  try {
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      return userData.id || null;
    }
  } catch {
    // Ignore errors when accessing localStorage
  }
  return null;
}

/**
 * Recovery utilities for error boundaries
 */
export const errorRecovery = {
  /**
   * Reload the current page
   */
  reloadPage() {
    window.location.reload();
  },

  /**
   * Navigate to home page
   */
  goHome() {
    window.location.href = '/';
  },

  /**
   * Navigate to dashboard if authenticated, otherwise to landing page
   */
  goToSafePage() {
    const token = localStorage.getItem('authToken');
    window.location.href = token ? '/app' : '/';
  },

  /**
   * Clear all local storage and reload
   */
  resetApplication() {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  },
};
